import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Trash2, Plus, Minus } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { API, useAuth } from '../App';

const CartPage = () => {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [cart, setCart] = useState({ items: [] });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchCart();
  }, []);

  const fetchCart = async () => {
    try {
      const response = await axios.get(`${API}/cart`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCart(response.data);
    } catch (error) {
      console.error('Failed to fetch cart', error);
      toast.error('Failed to load cart');
    } finally {
      setLoading(false);
    }
  };

  const updateQuantity = async (index, newQuantity) => {
    if (newQuantity < 1) return;
    
    const updatedItems = [...cart.items];
    updatedItems[index].quantity = newQuantity;
    
    try {
      await axios.post(`${API}/cart`, updatedItems, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCart({ ...cart, items: updatedItems });
    } catch (error) {
      console.error('Failed to update cart', error);
      toast.error('Failed to update cart');
    }
  };

  const removeItem = async (index) => {
    const updatedItems = cart.items.filter((_, i) => i !== index);
    
    try {
      await axios.post(`${API}/cart`, updatedItems, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCart({ ...cart, items: updatedItems });
      toast.success('Item removed from cart');
    } catch (error) {
      console.error('Failed to remove item', error);
      toast.error('Failed to remove item');
    }
  };

  const calculateTotal = () => {
    return cart.items.reduce((sum, item) => sum + (item.price * item.quantity), 0);
  };

  const subtotal = calculateTotal();
  const gstAmount = subtotal * 0.18;
  const shippingCharges = cart.items.reduce((sum, item) => sum + (500 * item.quantity), 0);
  const grandTotal = subtotal + gstAmount + shippingCharges;

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center">Loading...</div>;
  }

  if (cart.items.length === 0) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center py-12" data-testid="empty-cart">
        <h2 className="text-3xl font-heading font-bold mb-4">Your Cart is Empty</h2>
        <p className="text-muted-foreground mb-8">Add some beautiful lehengas to your cart</p>
        <button
          onClick={() => navigate('/products')}
          className="bg-primary text-primary-foreground hover:bg-primary/90 px-8 py-3 font-medium transition-all duration-300"
          data-testid="browse-products-button"
        >
          Browse Products
        </button>
      </div>
    );
  }

  return (
    <div className="min-h-screen py-12" data-testid="cart-page">
      <div className="max-w-[1400px] mx-auto px-6 md:px-12">
        <h1 className="text-4xl md:text-5xl font-heading font-bold mb-12">Shopping Cart</h1>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
          {/* Cart Items */}
          <div className="lg:col-span-2 space-y-6">
            {cart.items.map((item, index) => (
              <div
                key={index}
                className="bg-white border border-border p-6 flex flex-col sm:flex-row gap-6"
                data-testid={`cart-item-${index}`}
              >
                <div className="w-full sm:w-32 h-40 bg-accent overflow-hidden flex-shrink-0">
                  <img src={item.image} alt={item.product_name} className="w-full h-full object-cover" />
                </div>
                
                <div className="flex-1">
                  <h3 className="text-xl font-heading font-semibold mb-2">{item.product_name}</h3>
                  <p className="text-sm text-muted-foreground mb-4">Design No: {item.variant_sku}</p>
                  <p className="text-lg font-semibold text-luxury-accentGold">&#8377;{item.price.toFixed(2)}</p>
                </div>

                <div className="flex sm:flex-col items-center sm:items-end justify-between sm:justify-start gap-4">
                  <div className="flex items-center space-x-2 border border-border">
                    <button
                      onClick={() => updateQuantity(index, item.quantity - 1)}
                      className="w-8 h-8 hover:bg-accent flex items-center justify-center transition-colors"
                      data-testid={`decrease-quantity-${index}`}
                    >
                      <Minus size={16} />
                    </button>
                    <span className="w-12 text-center font-semibold" data-testid={`quantity-${index}`}>{item.quantity}</span>
                    <button
                      onClick={() => updateQuantity(index, item.quantity + 1)}
                      className="w-8 h-8 hover:bg-accent flex items-center justify-center transition-colors"
                      data-testid={`increase-quantity-${index}`}
                    >
                      <Plus size={16} />
                    </button>
                  </div>
                  
                  <button
                    onClick={() => removeItem(index)}
                    className="text-destructive hover:text-destructive/80 transition-colors"
                    data-testid={`remove-item-${index}`}
                  >
                    <Trash2 size={20} />
                  </button>
                </div>
              </div>
            ))}
          </div>

          {/* Order Summary */}
          <div className="lg:col-span-1">
            <div className="bg-white border border-border p-6 sticky top-24">
              <h2 className="text-2xl font-heading font-semibold mb-6">Order Summary</h2>
              
              <div className="space-y-4 mb-6">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Subtotal</span>
                  <span className="font-semibold">&#8377;{subtotal.toFixed(2)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">GST (18%)</span>
                  <span className="font-semibold">&#8377;{gstAmount.toFixed(2)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Shipping (&#8377;500/pc)</span>
                  <span className="font-semibold">&#8377;{shippingCharges.toFixed(2)}</span>
                </div>
                <div className="border-t border-border pt-4">
                  <div className="flex justify-between text-lg">
                    <span className="font-semibold">Grand Total</span>
                    <span className="font-bold text-luxury-accentGold" data-testid="cart-total">&#8377;{grandTotal.toFixed(2)}</span>
                  </div>
                </div>
              </div>

              <button
                onClick={() => navigate('/checkout')}
                className="w-full bg-primary text-primary-foreground hover:bg-primary/90 py-4 font-medium transition-all duration-300 shadow-sm hover:shadow-md"
                data-testid="proceed-to-checkout"
              >
                Proceed to Checkout
              </button>

              <button
                onClick={() => navigate('/products')}
                className="w-full mt-4 border-2 border-border hover:border-primary text-foreground py-3 font-medium transition-all duration-300"
                data-testid="continue-shopping"
              >
                Continue Shopping
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CartPage;
